import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import {
  GapStoreService,
  IGapDayPair,
} from '@principle-theorem/ng-principle-shared';
import { MOMENT_DATEPICKER_PROVIDERS } from '@principle-theorem/ng-shared';
import { IScheduleSummaryEventable } from '@principle-theorem/principle-core/interfaces';
import {
  CASUAL_DATE_WITH_YEAR,
  TIME_FORMAT,
  multiSort,
  sortTimestampAsc,
  toISODate,
} from '@principle-theorem/shared';
import { groupBy, mapValues, sortBy } from 'lodash';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'pr-gap-sidebar',
  templateUrl: './gap-sidebar.component.html',
  styleUrls: ['./gap-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  standalone: false,
})
export class GapSidebarComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  groupedGaps$: Observable<IGapDayPair[]>;
  groupedPendingGaps$: Observable<IGapDayPair[]>;
  dateFormat = CASUAL_DATE_WITH_YEAR;
  timeFormat = TIME_FORMAT;

  constructor(public gapStore: GapStoreService) {
    const gaps$ = combineLatest([
      this.gapStore.filteredGaps$,
      this.gapStore.searchResults$,
    ]).pipe(
      map(([gaps, searchResults]) =>
        searchResults.length ? searchResults : gaps
      ),
      multiSort((aGap, bGap) =>
        sortTimestampAsc(aGap.event.from, bGap.event.from)
      )
    );

    this.groupedGaps$ = gaps$.pipe(
      map((sortedGaps) => this._groupGapsByDay(sortedGaps))
    );

    this.groupedPendingGaps$ = this.gapStore.pendingGaps$.pipe(
      map((gaps) => this._groupGapsByDay(gaps))
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  private _groupGapsByDay(gaps: IScheduleSummaryEventable[]): IGapDayPair[] {
    const gapsWithDay = gaps.map((gap) => ({
      day: toISODate(gap.event.from),
      gap,
    }));

    const groupedPairs = groupBy(gapsWithDay, 'day');
    const dayGapsGroups = mapValues(groupedPairs, (pairs) => ({
      day: pairs[0].day,
      gaps: pairs.map((pair) => pair.gap),
    }));

    return sortBy(Object.values(dayGapsGroups), (group) => group.day);
  }
}
